import { useEffect, useState, type FormEvent } from "react";
import { post, ApiError } from "../lib/api.ts";
import { t, useLang, setLang, getLang } from "../lib/i18n.ts";
import { navigate } from "../lib/router.ts";
import { boot } from "../lib/store.ts";
import { Field } from "./common.tsx";

function Shell({ children }: { children: React.ReactNode }) {
  useLang();
  return (
    <div className="auth">
      <div className="auth-card">
        <div className="brand"><span className="logo" aria-hidden="true">م</span><div><b>{t("appName")}</b><small>{t("tagline")}</small></div></div>
        {children}
        <button className="lang-switch" onClick={() => setLang(getLang() === "ar" ? "en" : "ar")}>{getLang() === "ar" ? "English" : "العربية"}</button>
      </div>
    </div>
  );
}

function useForm() {
  const [err, setErr] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setErr(null);
    try { await fn(); } catch (e) { setErr(e instanceof ApiError ? e : new ApiError(0, "error", t("error"))); } finally { setBusy(false); }
  };
  return { err, busy, run };
}

export function Login({ next }: { next: string }) {
  const f = useForm();
  const [login, setLogin] = useState(""); const [password, setPassword] = useState("");
  const [ticket, setTicket] = useState<string | null>(null);
  const done = async () => { await boot(); navigate(next || "/channels/@me", true); };
  const submit = (e: FormEvent) => { e.preventDefault(); void f.run(async () => {
    const r = await post<{ mfaRequired?: boolean; ticket?: string }>("/auth/login", { login, password });
    if (r.mfaRequired && r.ticket) { setTicket(r.ticket); setPassword(""); return; }
    await done();
  }); };
  if (ticket) return <Shell><MfaStep ticket={ticket} onDone={done} onCancel={() => setTicket(null)} /></Shell>;
  return (
    <Shell>
      <form onSubmit={submit} className="stack" noValidate>
        <h1>{t("signIn")}</h1>
        <Field label={t("emailOrUsername")}><input id="login" autoComplete="username" value={login} onChange={(e) => setLogin(e.target.value)} required dir="auto" /></Field>
        <Field label={t("password")}><input id="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required /></Field>
        {f.err && <p className="err" role="alert">{f.err.message}</p>}
        <button className="btn pri block" disabled={f.busy || !login || !password}>{t("signIn")}</button>
        <div className="row between small">
          <a href="/forgot" onClick={(e) => { e.preventDefault(); navigate("/forgot"); }}>{t("forgotPassword")}</a>
          <span>{t("noAccount")} <a href="/register" onClick={(e) => { e.preventDefault(); navigate(`/register${next ? `?next=${encodeURIComponent(next)}` : ""}`); }}>{t("signUp")}</a></span>
        </div>
      </form>
    </Shell>
  );
}

function MfaStep({ ticket, onDone, onCancel }: { ticket: string; onDone: () => Promise<void>; onCancel: () => void }) {
  const f = useForm();
  const [recovery, setRecovery] = useState(false); const [code, setCode] = useState(""); const [remember, setRemember] = useState(false);
  const expired = f.err?.code === "ticket_expired";
  const submit = (e: FormEvent) => { e.preventDefault(); void f.run(async () => {
    await post("/auth/login/mfa", recovery ? { ticket, recoveryCode: code.trim(), rememberDevice: remember } : { ticket, code: code.replace(/\s/g, ""), rememberDevice: remember });
    await onDone();
  }); };
  return (
    <form onSubmit={submit} className="stack" noValidate>
      <h1>{t("mfaTitle")}</h1>
      <p className="muted small">{recovery ? t("mfaRecoveryHint") : t("mfaHint")}</p>
      <Field label={recovery ? t("recoveryCode") : t("authCode")}>
        <input id="mfa-code" autoFocus dir="ltr" autoComplete="one-time-code" inputMode={recovery ? "text" : "numeric"} maxLength={recovery ? 32 : 7}
          value={code} onChange={(e) => setCode(e.target.value)} placeholder={recovery ? "xxxxx-xxxxx" : "123456"} /></Field>
      <label className="check"><input type="checkbox" id="mfa-remember" checked={remember} onChange={(e) => setRemember(e.target.checked)} /> {t("rememberDevice")}</label>
      {f.err && <p className="err" role="alert">{expired ? t("mfaExpired") : f.err.message}</p>}
      {expired ? <button type="button" className="btn pri block" onClick={onCancel}>{t("backToSignIn")}</button>
        : <button className="btn pri block" disabled={f.busy || (recovery ? code.trim().length < 8 : code.replace(/\s/g, "").length !== 6)}>{t("verify")}</button>}
      <div className="row between small">
        <button type="button" className="lnk" onClick={() => { setRecovery(!recovery); setCode(""); }}>{recovery ? t("useAuthApp") : t("useRecoveryCode")}</button>
        <button type="button" className="lnk" onClick={onCancel}>{t("backToSignIn")}</button>
      </div>
    </form>
  );
}

export function ConfirmEmail({ token, signedIn }: { token: string; signedIn: boolean }) {
  const [state, setState] = useState<{ ok: boolean; msg: string } | null>(null);
  useEffect(() => { post<{ email: string }>("/auth/email/confirm", { token }).then((r) => setState({ ok: true, msg: t("emailChangedTo", { email: r.email }) }),
    (e) => setState({ ok: false, msg: e instanceof ApiError ? e.message : t("error") })); }, [token]);
  return (
    <Shell>
      <div className="stack center">
        <h1>{t("changeEmail")}</h1>
        {!state ? <p className="muted">…</p> : <p className={state.ok ? "ok" : "err"} role="status" dir="auto">{state.msg}</p>}
        {state && <button className="btn pri block" onClick={() => { void boot(); navigate(signedIn ? "/channels/@me" : "/login", true); }}>{t("continue")}</button>}
      </div>
    </Shell>
  );
}

export function Register({ next }: { next: string }) {
  const f = useForm();
  const [v, setV] = useState({ email: "", username: "", displayName: "", password: "" });
  const upd = (k: keyof typeof v) => (e: React.ChangeEvent<HTMLInputElement>) => setV({ ...v, [k]: e.target.value });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void f.run(async () => {
      await post("/auth/register", { ...v, username: v.username.trim().toLowerCase(), displayName: v.displayName.trim() || undefined, locale: getLang() });
      await boot(); navigate(next || "/channels/@me", true);
    });
  };
  const fe = (k: string) => f.err?.field(k);
  return (
    <Shell>
      <form onSubmit={submit} className="stack" noValidate>
        <h1>{t("signUp")}</h1>
        <Field label={t("email")} error={fe("email")}><input id="email" type="email" autoComplete="email" value={v.email} onChange={upd("email")} required dir="ltr" /></Field>
        <Field label={t("username")} error={fe("username")} hint={t("usernameHint")}><input id="username" autoComplete="username" value={v.username} onChange={upd("username")} required dir="ltr" pattern="[a-z0-9_.]{2,32}" /></Field>
        <Field label={t("displayName")} error={fe("displayName")}><input id="displayName" value={v.displayName} onChange={upd("displayName")} maxLength={32} dir="auto" /></Field>
        <Field label={t("password")} error={fe("password")} hint={t("passwordHint")}><input id="new-password" type="password" autoComplete="new-password" value={v.password} onChange={upd("password")} required minLength={10} /></Field>
        {f.err && !f.err.issues.length && <p className="err" role="alert">{f.err.message}</p>}
        <button className="btn pri block" disabled={f.busy}>{t("signUp")}</button>
        <p className="small center">{t("haveAccount")} <a href="/login" onClick={(e) => { e.preventDefault(); navigate("/login"); }}>{t("signIn")}</a></p>
      </form>
    </Shell>
  );
}

export function Forgot() {
  const f = useForm(); const [email, setEmail] = useState(""); const [done, setDone] = useState(false);
  return (
    <Shell>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); void f.run(async () => { await post("/auth/password-reset", { email }); setDone(true); }); }}>
        <h1>{t("resetTitle")}</h1>
        {done ? <p className="ok">{t("resetSent")}</p> : <>
          <Field label={t("email")}><input id="reset-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required dir="ltr" /></Field>
          {f.err && <p className="err">{f.err.message}</p>}
          <button className="btn pri block" disabled={f.busy || !email}>{t("resetSend")}</button>
        </>}
        <a href="/login" className="small" onClick={(e) => { e.preventDefault(); navigate("/login"); }}>{t("backToSignIn")}</a>
      </form>
    </Shell>
  );
}

export function ResetPassword({ token }: { token: string }) {
  const f = useForm(); const [pw, setPw] = useState(""); const [done, setDone] = useState(false);
  return (
    <Shell>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); void f.run(async () => { await post("/auth/password-reset/confirm", { token, password: pw }); setDone(true); }); }}>
        <h1>{t("resetTitle")}</h1>
        {done ? <><p className="ok">{t("passwordChanged")}</p><button type="button" className="btn pri block" onClick={() => navigate("/login", true)}>{t("signIn")}</button></> : <>
          <Field label={t("newPassword")} hint={t("passwordHint")}><input id="reset-pw" type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} minLength={10} required /></Field>
          {f.err && <p className="err">{f.err.message}</p>}
          <button className="btn pri block" disabled={f.busy || pw.length < 10}>{t("setPassword")}</button>
        </>}
      </form>
    </Shell>
  );
}

export function VerifyEmail({ token, signedIn }: { token: string; signedIn: boolean }) {
  const [state, setState] = useState<"busy" | "ok" | "bad">("busy");
  useEffect(() => { post("/auth/verify-email", { token }).then(() => setState("ok"), () => setState("bad")); }, [token]);
  return (
    <Shell>
      <div className="stack">
        <h1>{t("email")}</h1>
        {state === "busy" ? <p className="muted">…</p> : <p className={state === "ok" ? "ok" : "err"}>{t(state === "ok" ? "verifyOk" : "verifyBad")}</p>}
        <button className="btn pri block" onClick={() => { void boot(); navigate(signedIn ? "/channels/@me" : "/login", true); }}>{t("continue")}</button>
      </div>
    </Shell>
  );
}

export { Shell as AuthShell };
